import React, { useEffect, useState } from "react"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded"
import { getClientSprintDetailedEstimationByPeriod } from "../../../../services/clientReport.service"
import StackProgressBar from "../../../../components/compounds/graphics/StackProgressBar.compund"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import {
  CLIENT_HORIZON_ESTIMATION_TYPES,
  COLOR_MAP,
} from "../../../../consts/modules/clientReport.consts"

const ClientSprintHorizonEstimation = ({
  refresh,
  sprintData,
  period,
  isPresentationMode = false,
  collapsible = false,
  extraData = {},
}) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const sprintId = isPresentationMode ? sprintData?.sprintId : sprintData?.id

  useEffect(() => {
    fetchSprintDetailedEstimation(sprintId, period)
  }, [sprintId, period, refresh])

  const fetchSprintDetailedEstimation = async (periodId, periodType) => {
    if (!periodId || !periodType) {
      setData(null)
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      const response = await getClientSprintDetailedEstimationByPeriod(
        periodId,
        periodType.toUpperCase()
      )

      if (!response?.success) {
        throw new Error(response?.message || "Error al obtener la información")
      }

      setData(response?.result || null)
    } catch (error) {
      console.error(error)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  const breakdown =
    extraData?.horizonEstimation?.breakdown ?? data?.breakdown ?? []

  const isToDevelopBreakdown = (item) => {
    return (
      item?.estimationType === "TO_DEVELOP" &&
      item.developed !== undefined &&
      item.remaining !== undefined
    )
  }

  const getBreakdownValue = (item) => {
    return item.value ?? 0
  }

  const horizonSeries = Object.values(CLIENT_HORIZON_ESTIMATION_TYPES).map(
    (config) => {
      const value = breakdown
        .filter((item) => config.estimationTypes.includes(item.estimationType))
        .reduce((sum, item) => {
          return sum + getBreakdownValue(item)
        }, 0)

      return {
        ...config,
        value,
      }
    }
  )

  const total = breakdown.reduce(
    (sum, item) => sum + getBreakdownValue(item),
    0
  )

  const getPercentage = (value) => {
    if (!total) return 0
    return Number(((value / total) * 100).toFixed(1))
  }

  const stackSeriesFinal = breakdown
    .map((item) => {
      const value = getBreakdownValue(item)

      return {
        label: item.label,
        value,
        percentage: getPercentage(value),
        color: COLOR_MAP[item.estimationType] || item.color,
      }
    })
    .filter((item) => item.value > 0)

  return (
    <>
      <ClientReportCard
        collapsible={collapsible}
        title={"Horizonte de estimaciones"}
        icon={
          <ManageSearchRoundedIcon
            className="clr-primary-highlight"
            fontSize="large"
          />
        }
        className="font-cairo flex-shrink-0 w-full"
      >
        <div className="flex flex-column gap-4">
          <div className="flex flex-wrap gap-1 w-full">
            {horizonSeries.map((item) => (
              <div
                key={item.horizonType}
                className="grid-column grid-column--rest"
              >
                <ValueWithLabelTemplate
                  label={item.label}
                  value={
                    <div className="flex align-items-baseline gap-2 horizon-value">
                      <div
                        className="horizon-color-box"
                        style={{
                          background: item.color,
                        }}
                      />

                      <span className="flex align-items-baseline gap-1">
                        {item.extraLabel && <span>{item.extraLabel}</span>}

                        <span className="value-with-unit">
                          {item.value}
                          <span className="unit"> {item.valueLabel}</span>
                        </span>
                      </span>
                    </div>
                  }
                />
              </div>
            ))}
          </div>

          <div>
            <StackProgressBar
              height={30}
              borderRadius="0 0 6px 6px"
              series={stackSeriesFinal}
            />
          </div>
        </div>
      </ClientReportCard>
    </>
  )
}

export default ClientSprintHorizonEstimation
